import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import '../../css/accounts/Payments.css';

// 🎯 Helper function to convert numeric amount into words (ESLint Clean)
const numberToWords = (num) => {
  if (!num || isNaN(num) || num <= 0) return '';
  
  const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const inWords = (n) => {
    const strNum = n.toString();
    if (strNum.length > 9) return 'overflow';
    const n_array = ('000000000' + strNum).substr(-9).match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!n_array) return '';
    
    let str = '';
    str += (Number(n_array[1]) !== 0) ? (a[Number(n_array[1])] || b[n_array[1][0]] + ' ' + a[n_array[1][1]]) + 'Crore ' : '';
    str += (Number(n_array[2]) !== 0) ? (a[Number(n_array[2])] || b[n_array[2][0]] + ' ' + a[n_array[2][1]]) + 'Lakh ' : '';
    str += (Number(n_array[3]) !== 0) ? (a[Number(n_array[3])] || b[n_array[3][0]] + ' ' + a[n_array[3][1]]) + 'Thousand ' : '';
    str += (Number(n_array[4]) !== 0) ? (a[Number(n_array[4])] || b[n_array[4][0]] + ' ' + a[n_array[4][1]]) + 'Hundred ' : '';
    str += (Number(n_array[5]) !== 0) ? ((str !== '') ? 'and ' : '') + (a[Number(n_array[5])] || b[n_array[5][0]] + ' ' + a[n_array[5][1]]) : '';
    return str;
  };

  const parts = num.toString().split('.');
  let words = inWords(Number(parts[0])) + 'Dirhams ';
  if (parts[1] && Number(parts[1]) > 0) {
    const fils = parts[1].padEnd(2, '0').substring(0, 2);
    words += 'and ' + inWords(Number(fils)) + 'Fils ';
  }
  return words.trim() + ' Only';
};

const Payments = () => {
  const [paymentType, setPaymentType] = useState('Ledger Payment'); // 'Ledger Payment' or 'Agent Payment'
  
  // Data lists
  const [activeExpenses, setActiveExpenses] = useState([]);
  const [agentsList, setAgentsList] = useState([]);
  const [ledgerPayments, setLedgerPayments] = useState([]);
  const [agentPayments, setAgentPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form States
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().split('T')[0]);
  const [voucherNo, setVoucherNo] = useState('LP01');
  const [selectedLedgerId, setSelectedLedgerId] = useState('');
  const [selectedLedgerInfo, setSelectedLedgerInfo] = useState(null);
  const [description, setDescription] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [payMode, setPayMode] = useState('By Cash');
  const [editingPayment, setEditingPayment] = useState(null);

  // Search & Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 1. Fetch Active Expenses from Firestore 'expenses'
  useEffect(() => {
    const qExp = query(collection(db, 'expenses'), orderBy('expName', 'asc'));
    const unsubExp = onSnapshot(qExp, (snapshot) => {
      const activeList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(exp => exp.status === 'Active');
      
      setActiveExpenses(activeList);
    });

    return () => unsubExp();
  }, []);

  // 2. Fetch Agents List from Firestore 'agents'
  useEffect(() => {
    const qAgents = query(collection(db, 'agents'), orderBy('agentName', 'asc'));
    const unsubAgents = onSnapshot(qAgents, (snapshot) => {
      const fetched = snapshot.docs.map(doc => ({
        id: doc.id,
        agentName: doc.data().agentName || 'Unnamed Agent',
        companyName: doc.data().companyName || doc.data().agencyName || 'Independent',
        ...doc.data()
      }));
      setAgentsList(fetched);
    });

    return () => unsubAgents();
  }, []);

  // 3. Fetch Ledger Payments List from Firestore 'ledgerPayments'
  useEffect(() => {
    const qPayments = query(collection(db, 'ledgerPayments'), orderBy('createdAt', 'desc'));
    const unsubPayments = onSnapshot(qPayments, (snapshot) => {
      const paymentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date || '',
        amount: Number(doc.data().amount) || 0
      }));

      setLedgerPayments(paymentsList);
      
      if (paymentType === 'Ledger Payment' && !editingPayment) {
        const nextNum = paymentsList.length + 1;
        setVoucherNo(`LP${nextNum < 10 ? '0' + nextNum : nextNum}`);
      }

      setLoading(false);
    });

    return () => unsubPayments();
  }, [editingPayment, paymentType]);

  // 4. Fetch Agent Payments List from Firestore 'agentsPayments'
  useEffect(() => {
    const qAgentPay = query(collection(db, 'agentsPayments'), orderBy('createdAt', 'desc'));
    const unsubAgentPay = onSnapshot(qAgentPay, (snapshot) => {
      const paymentsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date || '',
        amount: Number(doc.data().amount) || 0
      }));

      setAgentPayments(paymentsList);

      if (paymentType === 'Agent Payment' && !editingPayment) {
        const nextNum = paymentsList.length + 1;
        setVoucherNo(`AP${nextNum < 10 ? '0' + nextNum : nextNum}`);
      }
    });

    return () => unsubAgentPay();
  }, [editingPayment, paymentType]);

  // Handle Selection Change (Expense or Agent)
  const handleSelectionChange = (e) => {
    const selectedId = e.target.value;
    setSelectedLedgerId(selectedId);

    if (paymentType === 'Ledger Payment') {
      const found = activeExpenses.find(item => item.id === selectedId);
      setSelectedLedgerInfo(found || null);
    } else {
      const found = agentsList.find(item => item.id === selectedId);
      setSelectedLedgerInfo(found || null);
    }
  };

  // Reset Form
  const resetForm = (type = paymentType) => {
    setVoucherDate(new Date().toISOString().split('T')[0]);
    if (type === 'Ledger Payment') {
      const nextNum = ledgerPayments.length + 1;
      setVoucherNo(`LP${nextNum < 10 ? '0' + nextNum : nextNum}`);
    } else {
      const nextNum = agentPayments.length + 1;
      setVoucherNo(`AP${nextNum < 10 ? '0' + nextNum : nextNum}`);
    }
    setSelectedLedgerId('');
    setSelectedLedgerInfo(null);
    setDescription('');
    setPaymentAmount('');
    setPayMode('By Cash');
    setEditingPayment(null);
    setSearchTerm('');
    setCurrentPage(1);
  };

  // Switch Payment Type Toggle
  const handleTypeSwitch = (type) => {
    setPaymentType(type);
    resetForm(type);
  };

  // 🎯 SAVE or UPDATE PAYMENT VOUCHER
  const handleSubmitPayment = async (e) => {
    e.preventDefault();

    if (!selectedLedgerId) return alert(paymentType === 'Ledger Payment' ? "Please select a ledger!" : "Please select an agent!");
    if (!paymentAmount || Number(paymentAmount) <= 0) return alert("Please enter a valid payment amount!");

    const newAmount = Number(paymentAmount);

    try {
      if (paymentType === 'Ledger Payment') {
        // --- LEDGER PAYMENTS LOGIC ---
        if (editingPayment) {
          const oldAmount = Number(editingPayment.amount);
          const amountDiff = newAmount - oldAmount;

          await updateDoc(doc(db, 'ledgerPayments', editingPayment.id), {
            date: voucherDate,
            description: description.trim(),
            amount: newAmount,
            amountInWords: numberToWords(newAmount),
            payMode: payMode,
            updatedAt: serverTimestamp(),
          });

          const expenseRef = doc(db, 'expenses', editingPayment.ledgerId);
          const expSnap = await getDoc(expenseRef);
          if (expSnap.exists()) {
            const currentBal = Number(expSnap.data().closingBalance) || 0;
            await updateDoc(expenseRef, {
              closingBalance: currentBal + amountDiff
            });
          }

          alert("Ledger payment voucher updated successfully!");
        } else {
          await addDoc(collection(db, 'ledgerPayments'), {
            voucherNo: voucherNo,
            date: voucherDate,
            ledgerId: selectedLedgerInfo.id,
            expCode: selectedLedgerInfo.expCode,
            expName: selectedLedgerInfo.expName,
            description: description.trim(),
            amount: newAmount,
            amountInWords: numberToWords(newAmount),
            payMode: payMode,
            createdAt: serverTimestamp(),
          });

          const expenseRef = doc(db, 'expenses', selectedLedgerInfo.id);
          const currentBal = Number(selectedLedgerInfo.closingBalance) || 0;
          
          await updateDoc(expenseRef, {
            closingBalance: currentBal + newAmount
          });

          alert(`Ledger payment voucher ${voucherNo} saved and closing balance updated!`);
        }
      } else {
        // --- AGENTS PAYMENTS LOGIC ---
        if (editingPayment) {
          await updateDoc(doc(db, 'agentsPayments', editingPayment.id), {
            date: voucherDate,
            description: description.trim(),
            amount: newAmount,
            amountInWords: numberToWords(newAmount),
            payMode: payMode,
            updatedAt: serverTimestamp(),
          });

          alert("Agent payment voucher updated successfully!");
        } else {
          await addDoc(collection(db, 'agentsPayments'), {
            voucherNo: voucherNo,
            date: voucherDate,
            agentId: selectedLedgerInfo.id,
            agentName: selectedLedgerInfo.agentName,
            companyName: selectedLedgerInfo.companyName,
            description: description.trim(),
            amount: newAmount,
            amountInWords: numberToWords(newAmount),
            payMode: payMode,
            createdAt: serverTimestamp(),
          });

          alert(`Agent payment voucher ${voucherNo} saved successfully!`);
        }
      }

      resetForm();
    } catch (error) {
      alert("Error processing payment voucher: " + error.message);
    }
  };

  // 🎯 EDIT VOUCHER
  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setVoucherDate(payment.date);
    setVoucherNo(payment.voucherNo);

    if (paymentType === 'Ledger Payment') {
      setSelectedLedgerId(payment.ledgerId);
      const found = activeExpenses.find(item => item.id === payment.ledgerId);
      setSelectedLedgerInfo(found || { expCode: payment.expCode, expName: payment.expName, closingBalance: 'N/A' });
    } else {
      setSelectedLedgerId(payment.agentId);
      const found = agentsList.find(item => item.id === payment.agentId);
      setSelectedLedgerInfo(found || { agentName: payment.agentName, companyName: payment.companyName });
    }
    
    setDescription(payment.description || '');
    setPaymentAmount(payment.amount);
    setPayMode(payment.payMode || 'By Cash');
  };

  // 🎯 DELETE VOUCHER
  const handleDelete = async (payment) => {
    if (window.confirm(`Are you sure you want to delete voucher ${payment.voucherNo}?`)) {
      try {
        if (paymentType === 'Ledger Payment') {
          await deleteDoc(doc(db, 'ledgerPayments', payment.id));

          const expenseRef = doc(db, 'expenses', payment.ledgerId);
          const expSnap = await getDoc(expenseRef);
          if (expSnap.exists()) {
            const currentBal = Number(expSnap.data().closingBalance) || 0;
            const updatedBal = Math.max(0, currentBal - Number(payment.amount));
            await updateDoc(expenseRef, {
              closingBalance: updatedBal
            });
          }
        } else {
          await deleteDoc(doc(db, 'agentsPayments', payment.id));
        }

        alert(`Payment voucher ${payment.voucherNo} deleted successfully!`);
      } catch (error) {
        alert("Error deleting voucher: " + error.message);
      }
    }
  };

  // Active Dataset based on Tab
  const targetDataset = paymentType === 'Ledger Payment' ? ledgerPayments : agentPayments;

  // Filter & Pagination Logic
  const filteredPayments = targetDataset.filter(p => 
    p.voucherNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.expName && p.expName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.agentName && p.agentName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (p.companyName && p.companyName.toLowerCase().includes(searchTerm.toLowerCase())) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = filteredPayments.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="payments-container">
      <div className="payments-card">
        
        {/* Top Type Toggle */}
        <div className="payment-type-selector">
          <button 
            className={`type-btn ${paymentType === 'Ledger Payment' ? 'active' : ''}`}
            onClick={() => handleTypeSwitch('Ledger Payment')}
          >
            📒 Ledger Payment Voucher
          </button>
          <button 
            className={`type-btn ${paymentType === 'Agent Payment' ? 'active' : ''}`}
            onClick={() => handleTypeSwitch('Agent Payment')}
          >
            🚚 Agents Payment
          </button>
        </div>

        {/* Payment Voucher Form (Shared Layout for both Ledger and Agent) */}
        <div className="voucher-form-box">
          <div className="voucher-header">
            <h3>
              {editingPayment 
                ? `✏️ Edit ${paymentType} Voucher (${voucherNo})` 
                : `💳 ${paymentType} Voucher Entry`
              }
            </h3>
            <span className="voucher-badge">Voucher No: <strong>{voucherNo}</strong></span>
          </div>

          <form onSubmit={handleSubmitPayment} className="voucher-form">
            <div className="voucher-grid">
              
              <div className="form-group">
                <label>Voucher Date <span className="req">*</span></label>
                <input 
                  type="date" 
                  value={voucherDate}
                  onChange={(e) => setVoucherDate(e.target.value)}
                  required
                />
              </div>

              {/* Dynamic Ledger/Agent Dropdown */}
              <div className="form-group">
                <label>
                  {paymentType === 'Ledger Payment' ? 'Select Expense Ledger' : 'Select Agent'} <span className="req">*</span>
                </label>
                <select 
                  value={selectedLedgerId} 
                  onChange={handleSelectionChange}
                  disabled={editingPayment !== null}
                  required
                >
                  <option value="">
                    {paymentType === 'Ledger Payment' ? '-- Choose Active Ledger --' : '-- Choose Agent --'}
                  </option>

                  {paymentType === 'Ledger Payment' ? (
                    activeExpenses.map((exp) => (
                      <option key={exp.id} value={exp.id}>
                        {exp.expCode} - {exp.expName}
                      </option>
                    ))
                  ) : (
                    agentsList.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.agentName} ({agent.companyName})
                      </option>
                    ))
                  )}
                </select>
              </div>

              {/* Display Closing Balance (Ledger Payment Only) */}
              {paymentType === 'Ledger Payment' && selectedLedgerInfo && (
                <div className="form-group balance-info-box">
                  <label>Current Closing Balance</label>
                  <div className="balance-display">
                    AED {Number(selectedLedgerInfo.closingBalance || 0).toFixed(2)}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Pay Mode <span className="req">*</span></label>
                <select value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                  <option value="By Cash">By Cash</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
              </div>

              <div className="form-group">
                <label>Payment Amount (AED) <span className="req">*</span></label>
                <input 
                  type="number" 
                  step="0.01"
                  placeholder="0.00"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  required
                />
              </div>

              {/* Amount in Words */}
              <div className="form-group full-width words-box">
                <label>Amount in Words:</label>
                <div className="words-text">
                  {paymentAmount ? numberToWords(paymentAmount) : "Zero Dirhams"}
                </div>
              </div>

              <div className="form-group full-width">
                <label>Particulars / Description</label>
                <input 
                  type="text" 
                  placeholder="Enter payment notes, bill reference, or remarks..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>

            </div>

            <div className="form-actions">
              <button type="submit" className="btn-save-voucher">
                {editingPayment ? "Update Payment Voucher" : "Save & Post Payment"}
              </button>
              {editingPayment && (
                <button type="button" onClick={() => resetForm()} className="btn-cancel">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Filter Bar */}
        <div className="payments-filter-bar">
          <input 
            type="text" 
            placeholder={paymentType === 'Ledger Payment' 
              ? "Search Vouchers by No, Ledger Name, or Description..." 
              : "Search Vouchers by No, Agent Name, Company, or Description..."
            } 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="payments-input"
          />
        </div>

        {/* Voucher History Table */}
        <div className="payments-table-wrapper">
          <table className="payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Voucher No</th>
                <th>{paymentType === 'Ledger Payment' ? 'Ledger Code & Name' : 'Agent & Company Name'}</th>
                <th>Particulars / Description</th>
                <th>Pay Mode</th>
                <th style={{ textAlign: 'right' }}>Amount (AED)</th>
                <th style={{ textAlign: 'center', width: '130px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="status-cell">Loading payment vouchers...</td></tr>
              ) : currentPayments.length === 0 ? (
                <tr><td colSpan="7" className="status-cell">No {paymentType.toLowerCase()} vouchers recorded yet.</td></tr>
              ) : (
                currentPayments.map((p) => (
                  <tr key={p.id} className={editingPayment?.id === p.id ? 'editing-row' : ''}>
                    <td>{p.date}</td>
                    <td className="voucher-code">{p.voucherNo}</td>
                    <td className="ledger-name-cell">
                      {paymentType === 'Ledger Payment' ? (
                        <><strong>{p.expCode}</strong> - {p.expName}</>
                      ) : (
                        <><strong>{p.agentName}</strong> ({p.companyName || 'Independent'})</>
                      )}
                    </td>
                    <td className="desc-cell">{p.description || '-'}</td>
                    <td>
                      <span className={`pay-mode-chip ${p.payMode === 'By Cash' ? 'cash' : 'bank'}`}>
                        {p.payMode}
                      </span>
                    </td>
                    <td className="currency-cell" style={{ textAlign: 'right' }}>
                      AED {p.amount.toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="action-buttons">
                        <button onClick={() => handleEdit(p)} className="btn-edit-sm">✏️ Edit</button>
                        <button onClick={() => handleDelete(p)} className="btn-delete-sm">🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {!loading && filteredPayments.length > 0 && (
          <div className="payments-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredPayments.length)}</strong> of <strong>{filteredPayments.length}</strong> vouchers
            </div>

            <div className="pagination-buttons">
              <button 
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prev = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prev && page - prev > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => setCurrentPage(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button 
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Payments;